import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Paper, Typography, Button, TextField, MenuItem, Table, TableHead,
  TableRow, TableCell, TableBody, Dialog, DialogTitle, DialogContent,
  DialogActions, IconButton, Chip, Tooltip, Stack, Switch, InputAdornment,
  FormControlLabel,
} from '@mui/material';
import AddIcon    from '@mui/icons-material/Add';
import EditIcon   from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import SearchIcon from '@mui/icons-material/Search';
import DescriptionIcon from '@mui/icons-material/Description';
import apiService from '../services/apiServices';
import useConfirm from '../hooks/useConfirm';

const API   = process.env.REACT_APP_API_URL || '';
function auth() {
  try {
    const raw = sessionStorage.getItem('logedInUser');
    if (raw) return JSON.parse(raw)?.token || '';
    const ck = document.cookie.split(';').find(c => c.trim().startsWith('logedInUser='));
    if (ck) return JSON.parse(decodeURIComponent(ck.split('=').slice(1).join('=')))?.token || '';
    return JSON.parse(localStorage.getItem('logedInUser') || '{}')?.token || '';
  } catch { return ''; }
}

const CATS = ['Identifikimi', 'Kontrata', 'Kualifikimi', 'Shëndetësi', 'Financiare', 'Tjera'];
const FMTS = ['pdf', 'jpg', 'jpeg', 'png', 'docx'];
const DAYS = [7, 14, 30, 60, 90];
const EMPTY = { Code: '', Name: '', Required: false, Category: 'Identifikimi', ReminderDays: [30, 7], AcceptedFormats: ['pdf'] };

export default function DocumentsPage() {
  const [docs,    setDocs]    = useState([]);
  const [form,    setForm]    = useState(EMPTY);
  const [dlgOpen, setDlgOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search,  setSearch]  = useState('');

  const load = useCallback(() => apiService().get('documentSettings').then(r => setDocs(r?.data || [])), []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() =>
    docs.filter(d => !search || (d.Name||'').toLowerCase().includes(search.toLowerCase()) || (d.Code||'').toLowerCase().includes(search.toLowerCase()) || (d.Category||'').toLowerCase().includes(search.toLowerCase()))
  , [docs, search]);

  const openAdd  = () => { setForm(EMPTY); setEditing(null); setDlgOpen(true); };
  const openEdit = (d) => { setForm({ ...d, ReminderDays: d.ReminderDays||[30,7], AcceptedFormats: d.AcceptedFormats||['pdf'] }); setEditing(d.Id||d.id); setDlgOpen(true); };

  const save = async () => {
    if (editing) await apiService().put('documentSettings', { ...form, id: editing });
    else         await apiService().post('documentSettings', form);
    setDlgOpen(false); load();
  };

  const [confirm, confirmUI] = useConfirm();


  const del = async (id) => {
    if (!(await confirm({ title: 'Fshi llojin e dokumentit', message: 'Fshij këtë lloj dokumenti?', confirmLabel: 'Fshi' }))) return;
    await fetch(`${API}/api/documentSettings/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${auth()}` } });
    load();
  };

  const toggleFmt = (fmt) => setForm(p => ({
    ...p,
    AcceptedFormats: p.AcceptedFormats.includes(fmt) ? p.AcceptedFormats.filter(f => f !== fmt) : [...p.AcceptedFormats, fmt],
  }));

  const toggleDay = (day) => setForm(p => ({
    ...p,
    ReminderDays: p.ReminderDays.includes(day) ? p.ReminderDays.filter(d => d !== day) : [...p.ReminderDays, day].sort((a,b) => b-a),
  }));

  return (
    <Box>
      {/* Toolbar */}
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" mb={2}>
        <TextField size="small" placeholder="Kërko…" value={search} onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ width: 220 }} />
        <Box flex={1} />
        <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openAdd}>Lloj i Ri</Button>
      </Stack>

      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell>Kodi</TableCell>
              <TableCell>Emri</TableCell>
              <TableCell>Kategoria</TableCell>
              <TableCell>Detyrueshëm</TableCell>
              <TableCell>Kujtesat (ditë)</TableCell>
              <TableCell>Formatet</TableCell>
              <TableCell width={80}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.length === 0 && (
              <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                <DescriptionIcon sx={{ fontSize: 48, opacity: 0.3, display: 'block', mx: 'auto', mb: 1 }} />
                Nuk ka lloje dokumentesh të konfiguruara.
              </TableCell></TableRow>
            )}
            {filtered.map(d => (
              <TableRow key={d.Id||d.id} hover>
                <TableCell><code>{d.Code}</code></TableCell>
                <TableCell>{d.Name}</TableCell>
                <TableCell>{d.Category}</TableCell>
                <TableCell>
                  <Chip size="small" label={d.Required ? 'Po' : 'Jo'} color={d.Required ? 'error' : 'default'} />
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap">
                    {(d.ReminderDays||[]).map(day => <Chip key={day} size="small" label={`${day}d`} variant="outlined" />)}
                  </Stack>
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap">
                    {(d.AcceptedFormats||[]).map(f => <Chip key={f} size="small" label={`.${f}`} variant="outlined" />)}
                  </Stack>
                </TableCell>
                <TableCell>
                  <Tooltip title="Edito"><IconButton size="small" onClick={() => openEdit(d)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                  <Tooltip title="Fshij"><IconButton size="small" onClick={() => del(d.Id||d.id)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      {/* Dialog */}
      <Dialog open={dlgOpen} onClose={() => setDlgOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edito Llojin e Dokumentit' : 'Lloj i Ri Dokumenti'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <Stack direction="row" spacing={1}>
              <TextField size="small" label="Kodi (p.sh. PASSPORT)" value={form.Code} onChange={e => setForm(p => ({...p, Code: e.target.value.toUpperCase()}))} sx={{ flex: 1 }} />
              <TextField size="small" label="Emri *" value={form.Name} onChange={e => setForm(p => ({...p, Name: e.target.value}))} sx={{ flex: 2 }} />
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField select size="small" label="Kategoria" value={form.Category} onChange={e => setForm(p => ({...p, Category: e.target.value}))} sx={{ flex: 1 }}>
                {CATS.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </TextField>
              <FormControlLabel control={<Switch checked={!!form.Required} onChange={e => setForm(p => ({...p, Required: e.target.checked}))} />} label="Detyrueshëm" />
            </Stack>
            <Box>
              <Typography variant="caption" color="text.secondary">Kujtesat (ditë para skadimit):</Typography>
              <Stack direction="row" spacing={0.5} mt={0.5}>
                {DAYS.map(d => (
                  <Chip key={d} size="small" label={`${d}d`} clickable
                    color={form.ReminderDays.includes(d) ? 'primary' : 'default'}
                    onClick={() => toggleDay(d)} />
                ))}
              </Stack>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Formatet e pranuara:</Typography>
              <Stack direction="row" spacing={0.5} mt={0.5}>
                {FMTS.map(f => (
                  <Chip key={f} size="small" label={`.${f}`} clickable
                    color={form.AcceptedFormats.includes(f) ? 'primary' : 'default'}
                    onClick={() => toggleFmt(f)} />
                ))}
              </Stack>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDlgOpen(false)}>Anulo</Button>
          <Button variant="contained" onClick={save} disabled={!form.Name}>Ruaj</Button>
        </DialogActions>
      </Dialog>
      {confirmUI}
    </Box>
  );
}
